<script lang="ts">
  /**
   * Banner com resumo da importação. Aparece no topo do dashboard para
   * o consultor confirmar de relance que os arquivos foram lidos com sucesso:
   * quantos colaboradores foram reconhecidos, quantas marcações vieram, fonte
   * (AFD / AEJ / ambos) e período. Quando 0 colaboradores são reconhecidos,
   * destaca em tom de alerta para que o usuário valide o arquivo enviado.
   */
  import type { CanonicalDataset } from '$core/domain/canonical.types';

  export let dataset: CanonicalDataset;

  $: employeeCount = dataset.employees.length;
  $: punchCount = dataset.punches.length;
  $: scheduleCount = dataset.schedules.length;

  $: hasAnyData = employeeCount > 0 || punchCount > 0;
  $: hasEmployees = employeeCount > 0;
  $: status = hasEmployees ? 'success' : hasAnyData ? 'warning' : 'error';

  /**
   * Detecta REPs-P que operam em modo batch (sempre offline). Quando >90% das
   * marcações chegam como tipo 7 (offline), explicamos que isso NÃO é um
   * problema do arquivo: vários REPs-P sincronizam em lote por design e
   * emitem todas as marcações como tipo 7. Sem esse aviso o consultor pode
   * concluir que algo está quebrado. Threshold 0.9 é conservador — datasets
   * mistos têm distribuições próximas de 50/50 ou 70/30.
   *
   * Veja audit/02-offline-investigation.md (Supricorp março/2026 — 100% tipo 7).
   */
  $: offlinePunchCount = dataset.punches.filter((p) => p.origin === 'afd-offline').length;
  $: offlineRatio = punchCount > 0 ? offlinePunchCount / punchCount : 0;
  $: showOfflineNotice = punchCount > 0 && offlineRatio > 0.9;

  /** Deriva a fonte a partir dos metadados de arquivos enviados (preferido)
   * ou, como fallback, da presença de escalas reais. */
  $: sourceLabel = (() => {
    const s = dataset.sources;
    if (s) {
      if (s.afdCount > 0) return s.afdCount === 1 ? 'AFD' : `${s.afdCount} AFDs`;
      return '—';
    }
    return 'AFD';
  })();

  /** Lista de nomes de arquivos para o tooltip/detalhe. */
  $: fileNames = (() => {
    const s = dataset.sources;
    if (!s) return [] as string[];
    return [...s.afdFileNames];
  })();

  function formatDate(iso: string): string {
    // YYYY-MM-DD → DD/MM/YYYY
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    return `${m[3]}/${m[2]}/${m[1]}`;
  }

  $: periodLabel = `${formatDate(dataset.period.start)} a ${formatDate(dataset.period.end)}`;

  $: statusMessage = hasEmployees
    ? 'Importação concluída. Confira os números abaixo.'
    : hasAnyData
      ? 'Arquivo lido, mas nenhum colaborador foi identificado nas marcações. Verifique se o arquivo está completo.'
      : 'Nenhum dado foi reconhecido no arquivo enviado. Verifique o formato e tente novamente.';

  $: statusIcon = hasEmployees ? '✓' : hasAnyData ? '⚠' : '✕';
</script>

<div class="import-summary import-summary--{status}" role="status" data-testid="import-summary">
  <div class="import-summary__header">
    <span class="import-summary__icon" aria-hidden="true">{statusIcon}</span>
    <div class="import-summary__title-wrap">
      <h3 class="import-summary__title">
        {employeeCount}
        {employeeCount === 1 ? 'colaborador reconhecido' : 'colaboradores reconhecidos'}
      </h3>
      <p class="import-summary__message">{statusMessage}</p>
    </div>
  </div>

  <dl class="import-summary__stats">
    <div class="import-summary__stat">
      <dt class="import-summary__stat-label">Marcações</dt>
      <dd class="import-summary__stat-value">{punchCount.toLocaleString('pt-BR')}</dd>
    </div>
    <div class="import-summary__stat">
      <dt class="import-summary__stat-label">Escalas</dt>
      <dd class="import-summary__stat-value">{scheduleCount.toLocaleString('pt-BR')}</dd>
    </div>
    <div class="import-summary__stat">
      <dt class="import-summary__stat-label">Fonte</dt>
      <dd class="import-summary__stat-value import-summary__stat-value--text">{sourceLabel}</dd>
    </div>
    <div class="import-summary__stat">
      <dt class="import-summary__stat-label">Período</dt>
      <dd class="import-summary__stat-value import-summary__stat-value--text">{periodLabel}</dd>
    </div>
  </dl>

  {#if fileNames.length > 0}
    <details class="import-summary__files">
      <summary class="import-summary__files-summary">
        Arquivos importados ({fileNames.length})
      </summary>
      <ul class="import-summary__files-list">
        {#each fileNames as fname (fname)}
          <li class="import-summary__files-item">{fname}</li>
        {/each}
      </ul>
    </details>
  {/if}

  {#if showOfflineNotice}
    <p class="import-summary__notice" data-testid="import-summary-offline-notice">
      <span class="import-summary__notice-icon" aria-hidden="true">ℹ</span>
      <span>
        <strong>{(offlineRatio * 100).toFixed(0)}% das marcações chegaram como tipo 7 (offline).</strong>
        Vários REP-P sincronizam em lote e emitem todas as marcações como offline por
        design — não é um defeito do arquivo. Os números do dashboard refletem as
        marcações reais; a distribuição online/offline é apenas informativa.
      </span>
    </p>
  {/if}
</div>

<style>
  .import-summary {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
    padding: var(--ptt-space-5);
    border-radius: var(--ptt-radius-md);
    border-left: 6px solid var(--ptt-gray-100);
    background: var(--ptt-gray-10);
  }

  .import-summary--success {
    border-left-color: #2e7d32;
    background: #e8f5e9;
  }

  .import-summary--warning {
    border-left-color: #f59e0b;
    background: #fff7ed;
  }

  .import-summary--error {
    border-left-color: #c62828;
    background: #ffebee;
  }

  .import-summary__header {
    display: flex;
    align-items: flex-start;
    gap: var(--ptt-space-3);
  }

  .import-summary__icon {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--ptt-white);
  }

  .import-summary--success .import-summary__icon {
    background: #2e7d32;
  }

  .import-summary--warning .import-summary__icon {
    background: #f59e0b;
  }

  .import-summary--error .import-summary__icon {
    background: #c62828;
  }

  .import-summary__title-wrap {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
  }

  .import-summary__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
    line-height: 1.2;
  }

  .import-summary__message {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--ptt-gray-200, #555);
    line-height: 1.4;
  }

  .import-summary__stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: var(--ptt-space-3);
    margin: 0;
    padding: var(--ptt-space-3) 0 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .import-summary__stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .import-summary__stat-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
    color: var(--ptt-gray-200, #555);
    margin: 0;
  }

  .import-summary__stat-value {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
    line-height: 1.1;
  }

  .import-summary__stat-value--text {
    font-size: 0.9375rem;
    font-weight: 600;
  }

  @media (max-width: 480px) {
    .import-summary__title {
      font-size: 1.25rem;
    }
  }

  .import-summary__files {
    margin-top: var(--ptt-space-2);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: var(--ptt-space-3);
  }

  .import-summary__files-summary {
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--ptt-gray-200, #555);
    user-select: none;
  }

  .import-summary__files-list {
    margin: var(--ptt-space-2) 0 0;
    padding-left: var(--ptt-space-4);
    list-style: disc;
  }

  .import-summary__files-item {
    font-size: 0.8125rem;
    font-family: var(--ptt-font-mono, monospace);
    color: var(--ptt-gray-300);
    word-break: break-all;
  }

  .import-summary__notice {
    margin: 0;
    padding: var(--ptt-space-3);
    border-radius: var(--ptt-radius-sm, 4px);
    background: rgba(245, 158, 11, 0.12);
    border: 1px solid rgba(245, 158, 11, 0.3);
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--ptt-gray-300);
    display: flex;
    align-items: flex-start;
    gap: var(--ptt-space-2);
  }

  .import-summary__notice-icon {
    flex-shrink: 0;
    font-weight: 700;
    color: #b45309;
  }
</style>
